// ============================================================
// GitHubRepos.jsx
//
// A branded, responsive course content page for the
// "GitHub Repositories" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Remote repo creation, cloning logic, and
//           upstream synchronization patterns.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import GitHubIcon             from "@mui/icons-material/GitHub";
import AddBoxIcon             from "@mui/icons-material/AddBox";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import PublishIcon            from "@mui/icons-material/Publish";
import SyncIcon               from "@mui/icons-material/Sync";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitHubRepos() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<GitHubIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Remote Cloud Hosting"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        GitHub <span>Repositories</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Think of a GitHub repository as your project's home in the cloud. It provides a central source of truth where teams can collaborate, track issues, and deploy code securely.
                    </Typography>
                </Box>

                {/* 1. CREATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AddBoxIcon />} label="1. Creating Your Remote Workspace" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Start by creating a container on GitHub. You can initialize it with a README, .gitignore, and license to set the project's foundation immediately.
                    </Typography>
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Grid container spacing={2}>
                            {["Navigate to github.com/new", "Enter a unique Repository Name", "Choose Public (everyone sees) or Private", "Click 'Create repository'"].map((step, i) => (
                                <Grid item xs={12} sm={6} key={i}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <Typography sx={{ color: BRAND.gold, fontWeight: 900 }}>{i + 1}.</Typography>
                                        <Typography sx={{ color: BRAND.white, fontSize: 13 }}>{step}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>

                {/* 2. CLONING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ContentCopyIcon />} label="2. Cloning to Local" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Cloning creates a full copy of the remote repository on your computer, including its entire history and all branches.
                    </Typography>
                    <CodeBlock
                        title="Git Clone Command"
                        code={`# Download the repository\ngit clone https://github.com/username/my-project.git\n\n# Navigate into the new folder\ncd my-project`}
                    />
                </Box>

                {/* 3. PUSHING LOCAL */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PublishIcon />} label="3. Pushing Existing Projects" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        If you already have a local project, follow this sequence to link it to a fresh GitHub repository.
                    </Typography>
                    <CodeBlock
                        title="Upstream Initialization"
                        code={`git init\ngit add .\ngit commit -m "feat: initial project setup"\n\n# Connect local to remote\ngit remote add origin https://github.com/user/repo.git\n\n# Push and set upstream tracking\ngit push -u origin main`}
                    />
                </Box>

                {/* 4. SYNCING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SyncIcon />} label="4. Continuous Synchronization" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        The <code>pull</code> and <code>push</code> cycle ensures that your local environment stays updated with the team's work in the cloud.
                    </Typography>
                    <CodeBlock
                        title="Sync Workflow"
                        code={`# Pull latest changes from GitHub\ngit pull origin main\n\n# Push your new snapshots to GitHub\ngit push origin main`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* REPO HIGHLIGHTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Repository Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Always include a README.md to explain your project's purpose.",
                            "Use a .gitignore file to prevent sensitive files from being pushed.",
                            "Commit small, logical chunks of work frequently.",
                            "Write clear, imperative commit messages (e.g., 'Fix bug' vs 'Fixed bug').",
                            "Pull latest changes before you start coding each day.",
                            "Use Private repositories for projects containing sensitive business logic."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Want to test a repo without cloning it? You can press the <strong>"."</strong> key while viewing any GitHub repository in your browser to open a web-based VS Code editor immediately!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
